import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Hexagon, Users, LayoutGrid, PenTool, BookOpen, Settings,
  ChevronDown, Shield, UserCog, HelpCircle, X,
  LogOut, Database, Building2
} from 'lucide-react';
import SMEList from './components/SMEList';
import SMEDetail from './components/SMEDetail';
import SMEForm from './components/SMEForm';
import EditorList from './components/EditorList';
import EditorDetail from './components/EditorDetail';
import EditorForm from './components/EditorForm';
import ArticleGenerator from './components/ArticleGenerator';
import PromptManager from './components/PromptManager';
import LexiconWorkspace from './components/LexiconWorkspace';
import AdminDashboard from './components/AdminDashboard';
import RagLauncher from './components/RagLauncher';
import LoginPage from './components/LoginPage';
import SMESidebar from './components/SMESidebar';
import { BrandedDialogProvider, useBrandedDialog } from './components/BrandedDialog';
import { authService, smeService, editorService } from './services/api';
import { AUTH_EXPIRED_EVENT, clearStoredSession, getUser, isAuthenticated, setStoredSession } from './services/auth';
import './App.css';

const navLinks = [
  { to: '/', label: 'SMEs', Icon: Users },
  { to: '/editors', label: 'Editors', Icon: PenTool },
  { to: '/generate-article', label: 'Content Studio', Icon: LayoutGrid },
  { to: '/lexicon', label: 'Lexicon', Icon: BookOpen },
  { to: '/prompts', label: 'Prompt Manager', Icon: Settings },
  { to: '/rag', label: 'RAG', Icon: Database },
];

const getRoleLabel = (roleVal) => {
  const normalized = String(roleVal || '').trim().replace(/[\s-]+/g, '_').toLowerCase();
  const labels = {
    super_admin: 'Super Admin',
    organizational_super_admin: 'Org Admin',
    organization_admin: 'Org Admin',
    org_admin: 'Org Admin',
    admin: 'Admin',
    content_admin: 'Content Admin',
    community_admin: 'Community Admin',
    user: 'User',
  };
  return labels[normalized] || String(roleVal || 'User');
};

const idCandidates = (value) => {
  if (!value) return [];
  if (typeof value === 'object') {
    return [
      value._id,
      value.id,
      value.organizationId,
      value.$oid,
      value.toString && value.toString !== Object.prototype.toString ? value.toString() : '',
    ].filter(Boolean).map(String);
  }
  return [String(value)];
};

const getSelectedOrgRole = (user) => {
  const organizationIds = new Set([
    ...idCandidates(user?.organizationId),
    ...idCandidates(user?.organization),
    ...idCandidates(user?.organization?._id),
    ...idCandidates(user?.organization?.id),
  ]);
  const rolePriority = [
    'super_admin',
    'organizational_super_admin',
    'organization_admin',
    'org_admin',
    'admin',
    'content_admin',
    'community_admin',
    'user',
  ];
  const assignments = Array.isArray(user?.roleAssignments) ? user.roleAssignments : [];
  const roles = [
    user?.role,
    user?.membershipRole,
    ...assignments
    .filter((assignment) => {
      if (!assignment?.role) return false;
      if (assignment.scopeType === 'global') return true;
      if (assignment.scopeType !== 'organization') return false;
      const assignmentScopeIds = idCandidates(assignment.scopeId);
      return !organizationIds.size || assignmentScopeIds.some((scopeId) => organizationIds.has(scopeId));
    })
    .map((assignment) => assignment.role),
  ].map((role) => String(role || '').trim().replace(/[\s-]+/g, '_').toLowerCase()).filter(Boolean);
  const assignedRole = rolePriority.find((role) => roles.includes(role));

  return assignedRole || 'user';
};

function AccountInfoModal({ type, user, onClose }) {
  if (!type) return null;

  const isSecurity = type === 'security';
  const organizationName = user?.organization?.name || user?.organizationName || 'Selected Encye organization';
  const role = getRoleLabel(getSelectedOrgRole(user));
  const title = isSecurity ? 'Security & Privacy' : 'Help & Support';
  const Icon = isSecurity ? Shield : HelpCircle;
  const sections = isSecurity
    ? [
      {
        title: 'Encye account access',
        items: [
          'Only active Encye users can access Agentic Article.',
          'Your session follows the Encye organization selected during login.',
          `Current organization: ${organizationName}.`,
        ],
      },
      {
        title: 'Organization data boundaries',
        items: [
          'Starter SMEs and editors are visible to every signed-in organization.',
          'New SMEs, editors, prompts, and generated content are saved under the selected Encye organization.',
          'Favorites, recently used SMEs, and My SMEs are private to your own user account.',
        ],
      },
      {
        title: 'RAG and publishing',
        items: [
          'RAG opens through the broker using your Encye session, then provisions you into the selected RAG organization.',
          'Publishing to Encye uses your RAG Encye connection, so attribution stays tied to your Encye account.',
          'Sign out when using a shared machine to clear the local Agentic Article session.',
        ],
      },
    ]
    : [
      {
        title: 'Content workflow',
        items: [
          'Use SMEs to define domain expertise and Editors to define review or writing style.',
          'Use Content Studio to generate articles from selected SMEs and editors.',
          'Use Lexicon for saved article work and reusable language assets.',
        ],
      },
      {
        title: 'RAG access',
        items: [
          'Open RAG from the top navigation and choose Selegic or SelegicCPQ.',
          'If RAG does not open, confirm the RAG broker is running and reachable from the configured backend URL.',
          'If Encye publishing fails inside RAG, reconnect Encye from the RAG interface.',
        ],
      },
      {
        title: 'Common fixes',
        items: [
          'If generation fails, confirm the article AI service is running on port 8001.',
          'If data looks missing, confirm you are logged into the expected Encye organization.',
          `Your current role is ${role}; admin permissions are required to manage shared SMEs, editors, and prompts.`,
        ],
      },
    ];

  return (
    <div className="account-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="account-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-modal-title"
        onMouseDown={event => event.stopPropagation()}
      >
        <div className="account-modal-header">
          <div className="account-modal-title-row">
            <span className="account-modal-icon"><Icon size={20} strokeWidth={2.2} /></span>
            <div>
              <h2 id="account-modal-title">{title}</h2>
              <p>{isSecurity ? 'How access, data ownership, and publishing attribution work.' : 'Operational guidance for using Agentic Article and RAG.'}</p>
            </div>
          </div>
          <button className="account-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={2.4} />
          </button>
        </div>

        <div className="account-modal-content">
          {sections.map(section => (
            <section className="account-modal-section" key={section.title}>
              <h3>{section.title}</h3>
              <ul>
                {section.items.map(item => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Profile Dropdown ── */
function OrganizationSwitchModal({ user, onClose, onSwitchOrganization, switchingOrgId }) {
  const currentOrganizationId = String(user?.organizationId || user?.organization?._id || user?.organization?.id || '');
  const memberships = Array.isArray(user?.memberships) ? user.memberships : [];
  const organizations = memberships
    .map((membership) => {
      const org = membership.organization || {};
      const organizationId = String(membership.organizationId || org._id || org.id || '');
      return {
        membership,
        organizationId,
        name: org.name || 'Organization',
        description: org.description || '--',
        role: getRoleLabel(membership.role || membership.membershipRole || 'user'),
        active: organizationId === currentOrganizationId,
      };
    })
    .filter((item) => item.organizationId)
    .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));

  return (
    <div className="org-switch-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="org-switch-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-switch-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="org-switch-modal-header">
          <div>
            <span className="org-switch-eyebrow">Organizations</span>
            <h2 id="org-switch-title">Choose workspace</h2>
            <p>Pick the organization you want to work in. Access, content, and tools will update for that workspace.</p>
          </div>
          <button type="button" className="org-switch-close" onClick={onClose} aria-label="Close organization switcher">
            <X size={18} strokeWidth={2.4} />
          </button>
        </div>

        <div className="org-switch-list">
          {organizations.map((item) => {
            const isSwitching = switchingOrgId === item.organizationId;
            return (
              <button
                type="button"
                key={item.organizationId}
                className={`org-switch-card ${item.active ? 'active' : ''}`}
                onClick={() => !item.active && onSwitchOrganization(item.organizationId)}
                disabled={item.active || Boolean(switchingOrgId)}
              >
                <span className="org-switch-card-icon">
                  <Building2 size={17} strokeWidth={2} />
                </span>
                <span className="org-switch-card-copy">
                  <span className="org-switch-card-title">
                    <strong>{item.name}</strong>
                    <span className="org-switch-card-role">{item.role}</span>
                  </span>
                  <small title={item.description}>{item.description}</small>
                </span>
                <span className="org-switch-card-action">
                  {isSwitching ? 'Switching...' : item.active ? 'Active' : 'Switch'}
                </span>
              </button>
            );
          })}
          {!organizations.length && (
            <div className="org-switch-empty">No active organizations are available for this account.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function ProfileDropdown({ user, initials, onClose, onLogout, onOpenInfo, onOpenOrganizationSwitch, onOpenAdmin, canOpenAdmin }) {
  const displayName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'Guest User';
  const email = user?.email ?? 'user@selegic.ai';

  const role = getRoleLabel(getSelectedOrgRole(user));
  const organizationName = user?.organization?.name || user?.organizationName || 'Selected Encye organization';
  const memberships = Array.isArray(user?.memberships) ? user.memberships : [];
  const canSwitchOrganization = memberships.length > 1;

  const menuItems = [
    { Icon: Shield, label: 'Security & Privacy', action: () => onOpenInfo('security') },
    { Icon: HelpCircle, label: 'Help & Support', action: () => onOpenInfo('help') },
  ];

  return (
    <div className="profile-dropdown">
      {/* Header strip */}
      <div className="profile-dropdown-header">
        <div className="profile-dropdown-header-top">
          <div className="profile-dropdown-avatar">{initials}</div>
          <div className="profile-dropdown-user-details">
            <div className="profile-dropdown-name-row">
              <p className="profile-dropdown-name">{displayName}</p>
              <span className="profile-dropdown-role">{role}</span>
            </div>
            <p className="profile-dropdown-email">{email}</p>
          </div>
        </div>

        {/* Connected Org Card - spans full width below user details */}
        <div className="profile-dropdown-org-card" title={`Connected to ${organizationName}`}>
          <div className="profile-dropdown-org-icon-wrap">
            <Building2 size={16} strokeWidth={2} />
          </div>
          <div className="profile-dropdown-org-copy">
            <span className="profile-dropdown-org-label">Connected organization</span>
            <strong>{organizationName}</strong>
          </div>
          <span className="profile-dropdown-org-dot" aria-hidden="true"></span>
        </div>

      </div>

      {/* Menu items */}
      <div className="profile-dropdown-menu">
        {canSwitchOrganization && (
          <button className="profile-menu-item" onClick={() => { onOpenOrganizationSwitch(); onClose(); }}>
            <Building2 size={15} strokeWidth={2} />
            Switch Organization
          </button>
        )}
        {canOpenAdmin && (
          <button className="profile-menu-item" onClick={() => { onOpenAdmin(); onClose(); }}>
            <UserCog size={15} strokeWidth={2} />
            Admin
          </button>
        )}
        {menuItems.map(({ Icon, label, action }) => (
          <button key={label} className="profile-menu-item" onClick={() => { action(); onClose(); }}>
            <Icon size={15} strokeWidth={2} />
            {label}
          </button>
        ))}
      </div>

      {/* Divider + Logout */}
      <div className="profile-dropdown-footer">
        <button className="profile-menu-item profile-menu-logout" onClick={onLogout}>
          <LogOut size={15} strokeWidth={2} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { alert } = useBrandedDialog();
  const [user, setUser] = useState(getUser());
  const canOpenAdmin = Boolean(user?.permissions?.canManageBasePrompt || user?.permissions?.canManageResourcePrompts);
  const [profileOpen, setProfileOpen] = useState(false);
  const [accountInfoModal, setAccountInfoModal] = useState(null);
  const [organizationSwitchOpen, setOrganizationSwitchOpen] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState('');
  const [sidebarSmes, setSidebarSmes] = useState([]);
  const [sidebarEditors, setSidebarEditors] = useState([]);
  const avatarRef = useRef(null);
  const searchParams = new URLSearchParams(location.search);
  const activeSidebarFilter = location.pathname === '/' || location.pathname === '/editors'
    ? searchParams.get('filter') || 'all'
    : '';

  /* Derive initials for the avatar */
  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || 'AK'
    : 'AK';

  /* Close dropdown when clicking outside */
  useEffect(() => {
    if (!profileOpen) return;
    const handleClick = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  useEffect(() => {
    let cancelled = false;
    authService.me()
      .then((response) => {
        const session = response.data?.data;
        if (!cancelled && session?.token && session?.user) {
          setStoredSession(session);
          setUser(session.user);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleAuthExpired = (event) => {
      setUser(null);
      navigate('/login', {
        replace: true,
        state: {
          message: event.detail?.message || 'Please login again',
        },
      });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, [navigate]);

  useEffect(() => {
    const fetchSidebarData = async () => {
      try {
        const response = await smeService.getAll();
        setSidebarSmes(response.data || []);
      } catch (error) {
        setSidebarSmes([]);
      }
      try {
        const response2 = await editorService.getAll();
        setSidebarEditors(response2.data || []);
      } catch (error) {
        setSidebarEditors([]);
      }
    };
    fetchSidebarData();
  }, []);

  const handleSidebarFilterChange = (filter) => {
    const basePath = location.pathname.startsWith('/editor') ? '/editors' : '/';
    const next = filter && filter !== 'all' ? `${basePath}?filter=${encodeURIComponent(filter)}` : basePath;
    navigate(next);
  };

  const handleLogout = () => {
    clearStoredSession();
    navigate('/login', { replace: true });
  };

  const handleSwitchOrganization = async (organizationId) => {
    if (!organizationId || switchingOrgId) return;
    setSwitchingOrgId(organizationId);
    try {
      const response = await authService.switchOrganization({ organizationId });
      const session = response.data?.data;
      if (!session?.token || !session?.user) {
        throw new Error('Organization switch response did not include a session');
      }
      setStoredSession(session);
      setProfileOpen(false);
      navigate('/', { replace: true });
      window.location.reload();
    } catch (error) {
      alert({
        title: 'Could not switch organization',
        message: error.response?.data?.error || error.message || 'Please try again.',
      });
    } finally {
      setSwitchingOrgId('');
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="app-header-row">

          {/* ── Logo + Brand ── */}
          <div className="app-header-brand">
            <div className="app-header-logo">
              <Hexagon size={28} strokeWidth={1.5} color="#0f7a4f" />
            </div>
            <div>
              <h1 className="app-header-title">SME Management System</h1>
              <p className="app-header-subtitle">Define AI agents that know your domain</p>
            </div>
          </div>

          {/* ── Nav Links ── */}
          <nav className="app-header-nav">
            {navLinks.map(({ to, label, Icon }) => {
              const isActive =
                to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`app-nav-link ${isActive ? 'active' : ''}`}
                >
                  <Icon size={15} strokeWidth={2} />
                  {label}
                </Link>
              );
            })}
          </nav>

          {/* ── Avatar + Dropdown ── */}
          <div className="app-header-avatar-wrap" ref={avatarRef}>
            <button
              className="app-header-avatar-btn"
              onClick={() => setProfileOpen(o => !o)}
              aria-label="Open profile menu"
            >
              <span className="app-header-avatar">{initials}</span>
              <ChevronDown
                size={14}
                strokeWidth={2.5}
                className={`avatar-chevron ${profileOpen ? 'open' : ''}`}
              />
            </button>

            {profileOpen && (
              <ProfileDropdown
                user={user}
                initials={initials}
                onClose={() => setProfileOpen(false)}
                onLogout={handleLogout}
                onOpenInfo={setAccountInfoModal}
                onOpenOrganizationSwitch={() => setOrganizationSwitchOpen(true)}
                onOpenAdmin={() => navigate('/admin')}
                canOpenAdmin={canOpenAdmin}
              />
            )}
          </div>

        </div>
      </header>

      <main className="app-shell-body">
        <SMESidebar
          smes={sidebarSmes}
          editors={sidebarEditors}
          activeFilter={activeSidebarFilter}
          onFilterChange={handleSidebarFilterChange}
          pathname={location.pathname}
        />
        <section className={
          location.pathname === '/' ||
            location.pathname === '/editors' ||
            location.pathname.startsWith('/sme/') ||
            location.pathname.startsWith('/editor/') ||
            location.pathname === '/generate-article' ||
            location.pathname === '/admin'
            ? 'app-main-full'
            : 'app-main'
        }>
          <Routes>
            <Route path="/" element={<SMEList />} />
            <Route path="/sme/:id" element={<SMEDetail />} />
            <Route path="/sme/new" element={<SMEForm />} />
            <Route path="/sme/:id/edit" element={<SMEForm />} />
            <Route path="/editors" element={<EditorList />} />
            <Route path="/editor/:id" element={<EditorDetail />} />
            <Route path="/editor/new" element={<EditorForm />} />
            <Route path="/editor/:id/edit" element={<EditorForm />} />
            <Route path="/generate-article" element={<ArticleGenerator />} />
            <Route path="/lexicon" element={<LexiconWorkspace />} />
            <Route path="/prompts" element={<PromptManager />} />
            <Route path="/rag" element={<RagLauncher />} />
            <Route path="/admin" element={canOpenAdmin ? <AdminDashboard /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </section>
      </main>
      <AccountInfoModal
        type={accountInfoModal}
        user={user}
        onClose={() => setAccountInfoModal(null)}
      />
      {organizationSwitchOpen && (
        <OrganizationSwitchModal
          user={user}
          onClose={() => setOrganizationSwitchOpen(false)}
          onSwitchOrganization={handleSwitchOrganization}
          switchingOrgId={switchingOrgId}
        />
      )}
    </div>
  );
}

function RequireAuth({ children }) {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname, message: 'Please login again' }} />;
  }
  return children;
}

function App() {
  return (
    <BrandedDialogProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={<RequireAuth><AppShell /></RequireAuth>} />
        </Routes>
      </Router>
    </BrandedDialogProvider>
  );
}

export default App;
